import { execSync } from "child_process"
import { existsSync } from "fs"
import {
  CLIError,
  type PackageManager,
  type PackageManagerCommands,
  type ProjectConfig,
} from "../types/types.js"
import { logger } from "./logger.js"

const packageManagers: Record<PackageManager, PackageManagerCommands> = {
  bun: {
    add: "bun add",
    addDev: "bun add -D",
    exec: "bunx",
    init: "bun init -y",
    install: "bun install",
    run: "bun run",
  },
  npm: {
    add: "npm install",
    addDev: "npm install -D",
    exec: "npx",
    init: "npm init -y",
    install: "npm install",
    run: "npm run",
  },
  pnpm: {
    add: "pnpm add",
    addDev: "pnpm add -D",
    exec: "pnpx",
    init: "pnpm init",
    install: "pnpm install",
    run: "pnpm run",
  },
  yarn: {
    add: "yarn add",
    addDev: "yarn add -D",
    exec: "yarn",
    init: "yarn init -y",
    install: "yarn install",
    run: "yarn",
  },
}

export class PackageManagerAdapter {
  private commands: PackageManagerCommands
  private readonly manager: PackageManager

  constructor(manager: PackageManager) {
    this.manager = manager
    this.commands = packageManagers[manager]
  }

  static detect(projectPath: string): PackageManager | null {
    const lockFiles: Record<string, PackageManager> = {
      "bun.lockb": "bun",
      "package-lock.json": "npm",
      "pnpm-lock.yaml": "pnpm",
      "yarn.lock": "yarn",
    }

    for (const [file, manager] of Object.entries(lockFiles)) {
      if (existsSync(`${projectPath}/${file}`)) return manager
    }

    return null
  }

  static isInstalled(pm: string): boolean {
    try {
      execSync(`${pm} --version`, { stdio: "ignore" })
      return true
    } catch {
      return false
    }
  }

  install(config: ProjectConfig): void {
    if (config.dryRun) {
      logger.debug(`[DRY RUN] Would run: ${this.commands.install}`)
      return
    }

    logger.info(`Installing dependencies with ${this.manager}...`)

    try {
      execSync(this.commands.install, {
        cwd: config.projectPath,
        stdio: config.debug ? "inherit" : "ignore",
      })
      logger.success("Dependencies installed successfully")
    } catch (error) {
      throw new CLIError(
        `Failed to install dependencies with ${this.manager}`,
        "INSTALL_FAILED",
        error,
      )
    }
  }

  add(packages: Array<string>, dev: boolean, config: ProjectConfig): void {
    const command = dev ? this.commands.addDev : this.commands.add
    const fullCommand = `${command} ${packages.join(" ")}`

    if (config.dryRun) {
      logger.debug(`[DRY RUN] Would run: ${fullCommand}`)
      return
    }

    logger.debug(`Running: ${fullCommand}`)

    try {
      execSync(fullCommand, {
        cwd: config.projectPath,
        stdio: config.debug ? "inherit" : "ignore",
      })
    } catch (error) {
      throw new CLIError(
        `Failed to add packages: ${packages.join(", ")}`,
        "ADD_PACKAGES_FAILED",
        error,
      )
    }
  }

  run(script: string, args: Array<string>, config: ProjectConfig): void {
    const command = `${this.commands.run} ${script} ${args.join(" ")}`.trim()

    if (config.dryRun) {
      logger.debug(`[DRY RUN] Would run: ${command}`)
      return
    }

    logger.debug(`Running: ${command}`)

    try {
      execSync(command, {
        cwd: config.projectPath,
        stdio: config.debug ? "inherit" : "ignore",
      })
    } catch (error) {
      throw new CLIError(
        `Failed to run script: ${script}`,
        "RUN_SCRIPT_FAILED",
        error,
      )
    }
  }

  exec(command: string, args: Array<string>, config: ProjectConfig): void {
    const fullCommand =
      `${this.commands.exec} ${command} ${args.join(" ")}`.trim()

    if (config.dryRun) {
      logger.debug(`[DRY RUN] Would run: ${fullCommand}`)
      return
    }

    logger.debug(`Running: ${fullCommand}`)

    try {
      execSync(fullCommand, {
        cwd: config.projectPath,
        stdio: config.debug ? "inherit" : "ignore",
      })
    } catch (error) {
      throw new CLIError(
        `Failed to execute command: ${command}`,
        "EXEC_FAILED",
        error,
      )
    }
  }

  getInstallCommand(): string {
    return this.commands.install
  }

  getRunCommand(script: string): string {
    return `${this.commands.run} ${script}`
  }

  getName(): PackageManager {
    return this.manager
  }
}
